import type { ReactNode } from 'react';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import Open from '../helpers/api/Open';
import Item from '../helpers/Item';
import { LocaleContext } from './LocaleContext';

type AvailableLanguage = {
  abbr: string;
  name: string;
};

export type Settings = {
  availableLanguages: AvailableLanguage[];
  builderEnabled: boolean;
  bookingShortcutCustomExpiredMessage: boolean;
  bookingShortcutCustomExpiredMessageContent: string | null;
  clientFields: Record<string, any>;
  clientViewWithoutBookingShortcut: boolean;
  callback_contact_options: Record<string, any>;
  callback_service_help: string;
  callsToAction: any[];
  customContact: boolean;
  customContactAdditionalMessage: string | null;
  customContactHeading: string | null;
  defaultLanguage: string;
  disabledDescription: string | null;
  disabledTitle: string | null;
  explicitComplianceAgreement: boolean;
  firstStep: string | null;
  googleRecaptchaEnabled: boolean;
  hideLocationHours: boolean;
  homepage: string | null;
  includeLocationStep: boolean;
  joinLineFromClientView: boolean;
  loading: boolean;
  locationSearchRadius: any;
  notificationsGeneralEvents: any;
  preBookingStatement: string | null;
  privacyPolicyUrl: string | null;
  receiveSmsNotifications: boolean;
  reorderSpecificStaffBeforeLanguages: boolean;
  serviceStepLayout: boolean;
  showLocationPhoneCallToAction: boolean;
  showStaffLanguages: boolean;
  showStaffPicker: boolean;
  showWaitTime: boolean;
  staffAvailableLanguages: string[];
  timeInAdvance: number | null;
  timeToCancel: number | null;
  timeToReschedule: number | null;
  termsOfServiceUrl: string | null;
  waitListEnabled: boolean;
  showCallbackWaitTime: boolean;
  showStaffPhotos: boolean;
  staffSelection: string | null;
  walkin_questions: any;
  useNearestLocation: boolean;
  kioskAppointmentBooking: boolean;
  kioskCustomMessage: string | null;
  kioskQrCode: boolean;
  kioskSelectPreferredLang: boolean;
  kioskSelectPreferredStaff: boolean;
  kioskShowCustomMessage: boolean;
};

const INITIAL_SETTINGS: Settings = {
  availableLanguages: [],
  bookingShortcutCustomExpiredMessage: false,
  bookingShortcutCustomExpiredMessageContent: null,
  builderEnabled: false,
  callback_contact_options: {},
  callback_service_help: '',
  callsToAction: [],
  clientFields: {},
  clientViewWithoutBookingShortcut: false,
  customContact: false,
  customContactAdditionalMessage: null,
  customContactHeading: null,
  defaultLanguage: '',
  disabledDescription: null,
  disabledTitle: null,
  explicitComplianceAgreement: false,
  firstStep: null,
  googleRecaptchaEnabled: false,
  hideLocationHours: false,
  homepage: null,
  includeLocationStep: false,
  joinLineFromClientView: false,
  kioskAppointmentBooking: false,
  kioskCustomMessage: null,
  kioskQrCode: false,
  kioskSelectPreferredLang: false,
  kioskSelectPreferredStaff: false,
  kioskShowCustomMessage: false,
  loading: true,
  locationSearchRadius: null,
  notificationsGeneralEvents: null,
  preBookingStatement: null,
  privacyPolicyUrl: null,
  receiveSmsNotifications: false,
  reorderSpecificStaffBeforeLanguages: false,
  serviceStepLayout: false,
  showCallbackWaitTime: false,
  showLocationPhoneCallToAction: false,
  showStaffLanguages: false,
  showStaffPhotos: false,
  showStaffPicker: false,
  showWaitTime: false,
  staffAvailableLanguages: [],
  staffSelection: null,
  termsOfServiceUrl: null,
  timeInAdvance: null,
  timeToCancel: null,
  timeToReschedule: null,
  useNearestLocation: false,
  waitListEnabled: false,
  walkin_questions: null,
};

export const SettingsContext = createContext<Settings>(INITIAL_SETTINGS);

type SettingsProviderProps = {
  children: ReactNode;
};

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const Api = useMemo(() => Open.api(), []);

  const [locale] = useContext(LocaleContext);
  const [settings, setSettings] = useReducer(
    (_: Settings, newState: Settings) => newState,
    INITIAL_SETTINGS,
  );

  useEffect(() => {
    Api.locale(locale)
      .settings()
      .get()
      .then(
        ({
          data: {
            data: { attributes },
          },
        }) => {
          setSettings({
            ...Item.keysToCamel(attributes),
            loading: false,
          });
        },
      );
  }, [Api, locale]);

  if (settings.loading) {
    return null;
  }

  return (
    <SettingsContext.Provider value={settings}>
      {children}
    </SettingsContext.Provider>
  );
};
